import { useEffect, useMemo, useState } from "react";
import { useAdmin } from "../admin";
import AudioField from "../components/AudioField";
import Illustration from "../components/Illustration";
import PhonePreview from "../components/PhonePreview";
import StatusChip from "../components/StatusChip";
import {
  emptyScenario,
  ILLUSTRATIONS,
  LANG_NAMES,
  LANGS,
  languageComplete,
  normalise,
  problems,
  QUALITIES,
  QUALITY_NAMES,
  ROLES,
  ROLE_NAMES,
  skillName,
  skillTag,
  slugify,
  SLUG,
  tidy,
} from "../lib/scenario";
import type { Lang, OptionText, Quality, ScenarioContent, ScenarioRow, SceneText } from "../lib/scenario";
import { getScenario, listScenarios } from "../lib/scenarios";
import { navigate, setLeaveGuard } from "../router";
import { errorText, supabase } from "../supabase";

// Screen 14: the training lead writes one scenario and its feedback, in Marathi, Hindi
// and English, with audio, and takes it through Draft, In review and Approved.

type Message = { kind: "ok" | "error" | "warn"; text: string; list?: string[] };

export default function Editor({ id }: { id: string | null }) {
  const { admin } = useAdmin();
  const [row, setRow] = useState<ScenarioRow | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [content, setContent] = useState<ScenarioContent>(emptyScenario);
  const [saved, setSaved] = useState(() => JSON.stringify(tidy(emptyScenario())));
  const [lang, setLang] = useState<Lang>("mr");
  const [slugTouched, setSlugTouched] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [busy, setBusy] = useState(false);
  const [knownSkills, setKnownSkills] = useState<string[]>([]);

  function show(r: ScenarioRow) {
    const c = normalise(r.scenario_change?.content ?? r.content);
    setRow(r);
    setContent(c);
    setSaved(JSON.stringify(tidy(c)));
  }

  useEffect(() => {
    if (id) {
      getScenario(id)
        .then((r) => (r ? show(r) : setMessage({ kind: "error", text: "This scenario was not found." })))
        .catch((e) => setMessage({ kind: "error", text: errorText(e) }))
        .finally(() => setLoading(false));
    }
    listScenarios()
      .then((rows) => setKnownSkills([...new Set(rows.flatMap((r) => r.skill_tags))].sort()))
      .catch(() => {});
  }, [id]);

  const dirty = JSON.stringify(tidy(content)) !== saved;
  useEffect(() => {
    setLeaveGuard(() => !dirty || window.confirm("You have unsaved changes. Leave without saving them?"));
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      setLeaveGuard(null);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [dirty]);

  const issues = useMemo(() => problems(tidy(content)), [content]);
  const isNew = !row;
  const pending = row?.scenario_change ?? null;
  const status = pending?.status ?? row?.status ?? "draft";
  const readOnly = row?.status === "retired";
  const hasAudio = useMemo(() => JSON.stringify(content).includes('"audio/'), [content]);
  const folder = (l: Lang) => (SLUG.test(content.id) ? `audio/${content.id}/${l}` : null);

  // Editing helpers ------------------------------------------------------------------
  const update = (change: (c: ScenarioContent) => void) =>
    setContent((c) => {
      const next = structuredClone(c);
      change(next);
      return next;
    });
  const setScene = (l: Lang, field: keyof SceneText, value: string | undefined) =>
    update((c) => {
      (c.text[l] as unknown as Record<string, unknown>)[field] = value;
      if (field === "title" && l === "en" && isNew && !slugTouched && !hasAudio) c.id = slugify(value ?? "");
    });
  const setOption = (i: number, l: Lang, field: keyof OptionText, value: unknown) =>
    update((c) => {
      (c.options[i].text[l] as unknown as Record<string, unknown>)[field] = value;
    });
  const setQuality = (i: number, q: Quality) =>
    update((c) => {
      // Swap with the response that had this marker, so there is always one of each.
      const other = c.options.findIndex((o) => o.quality === q);
      if (other >= 0) c.options[other].quality = c.options[i].quality;
      c.options[i].quality = q;
    });

  // Actions --------------------------------------------------------------------------
  async function save(): Promise<ScenarioRow | null> {
    const { data, error } = await supabase.rpc("admin_save_scenario", {
      p_id: row?.id ?? null,
      p_content: tidy(content),
    });
    if (error) {
      setMessage({ kind: "error", text: errorText(error) });
      return null;
    }
    const fresh = await getScenario((data as { id: string }).id);
    if (!fresh) return null;
    show(fresh);
    if (isNew) {
      setLeaveGuard(null);
      window.history.replaceState(null, "", `#/scenarios/${fresh.id}`);
    }
    return fresh;
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (e) {
      setMessage({ kind: "error", text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(target: ScenarioRow, next: string, done: string) {
    const { data, error } = await supabase.rpc("admin_set_scenario_status", { p_id: target.id, p_status: next });
    if (error) {
      setMessage({ kind: "error", text: errorText(error) });
      return;
    }
    const reply = data as { ok: boolean; problems?: string[] };
    if (!reply.ok) {
      setMessage({ kind: "warn", text: "Not yet. First:", list: reply.problems });
      return;
    }
    const fresh = await getScenario(target.id);
    if (fresh) show(fresh);
    setMessage({ kind: "ok", text: done });
  }

  const saveDraft = () =>
    run(async () => {
      const r = await save();
      if (r) {
        setMessage({
          kind: "ok",
          text:
            r.status === "approved"
              ? "Saved as changes. Phones keep the approved version until the changes are approved."
              : "Saved as a draft.",
        });
      }
    });
  const sendForReview = () =>
    run(async () => {
      const r = dirty || !row ? await save() : row;
      if (r) await setStatus(r, "in-review", "Sent for review.");
    });
  const approve = () =>
    run(async () => {
      if (row) {
        await setStatus(row, "approved", "Approved. Phones get it at their next sync, and the weekly cycle can pick it.");
      }
    });
  const backToDraft = () => run(async () => void (row && (await setStatus(row, "draft", "Back to draft."))));
  const restore = () =>
    run(async () => void (row && (await setStatus(row, "draft", "Restored to draft. You can edit it again."))));
  const discard = () =>
    run(async () => {
      if (!row) return;
      const { error } = await supabase.rpc("admin_discard_change", { p_id: row.id });
      if (error) throw error;
      const fresh = await getScenario(row.id);
      if (fresh) show(fresh);
      setMessage({ kind: "ok", text: "Changes thrown away. The approved version is unchanged." });
    });

  if (loading) return <p className="empty">Loading…</p>;
  if (id && !row) {
    return (
      <div className="stack">
        {message && <div className={`notice ${message.kind}`}>{message.text}</div>}
        <a href="#/scenarios">Back to scenarios</a>
      </div>
    );
  }

  const scene = content.text[lang];
  const picture = LANGS.map((l) => content.text[l].illustrationKey).find(Boolean);
  const miss = (value: string | undefined) => (!value?.trim() ? "missing" : "");

  return (
    <>
      <div className="page-head">
        <button className="link-btn" onClick={() => navigate("/scenarios")}>
          ← Scenarios
        </button>
        <h1>{isNew ? "New scenario" : content.text.en.title || content.text.mr.title || content.id}</h1>
        {row && <StatusChip row={row} />}
        {dirty && <span className="chip in-review">Unsaved changes</span>}
      </div>

      {row?.status === "approved" && (
        <div className="notice" style={{ marginBottom: 16 }}>
          {pending
            ? `Phones have the approved version ${row.version}. These changes reach phones once they are approved, as version ${row.version + 1}.`
            : "This scenario is approved and on phones. Saving an edit keeps the approved version on phones until the edit is approved too."}
        </div>
      )}
      {readOnly && (
        <div className="notice warn row" style={{ marginBottom: 16 }}>
          <span style={{ flex: 1 }}>This scenario is retired. It is no longer given out. Restore it to edit it.</span>
          <button className="btn small" disabled={busy} onClick={restore}>
            Restore to draft
          </button>
        </div>
      )}

      <div className="editor">
        <fieldset disabled={readOnly || busy} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
          <div className="stack" style={{ gap: 20 }}>
            <section className="card stack">
              <h2>About this scenario</h2>
              <label className="field">
                Id
                <span className="hint">
                  Lowercase words joined by hyphens. Phones use it to keep practice together, so it cannot change once
                  saved.
                </span>
                <input
                  type="text"
                  value={content.id}
                  disabled={!isNew || hasAudio}
                  className={SLUG.test(content.id) ? "" : "missing"}
                  onChange={(e) => {
                    setSlugTouched(true);
                    update((c) => {
                      c.id = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-");
                    });
                  }}
                />
              </label>
              <div className="field">
                Who practises it
                <div className="row">
                  {ROLES.map((r) => (
                    <label key={r} className="check">
                      <input
                        type="checkbox"
                        checked={content.roles.includes(r)}
                        onChange={(e) =>
                          update((c) => {
                            c.roles = e.target.checked
                              ? ROLES.filter((x) => x === r || c.roles.includes(x))
                              : c.roles.filter((x) => x !== r);
                          })
                        }
                      />
                      {ROLE_NAMES[r]}
                    </label>
                  ))}
                </div>
              </div>
              <Skills
                tags={content.skillTags}
                known={knownSkills}
                onChange={(tags) =>
                  update((c) => {
                    c.skillTags = tags;
                  })
                }
              />
              <label className="field" style={{ maxWidth: 200 }}>
                Minutes it takes
                <input
                  type="number"
                  min={1}
                  max={15}
                  value={content.estMinutes}
                  onChange={(e) =>
                    update((c) => {
                      c.estMinutes = Number(e.target.value);
                    })
                  }
                />
              </label>
              <div className="field">
                Picture
                <div className="pictures">
                  {ILLUSTRATIONS.map((p) => (
                    <button
                      type="button"
                      key={p.key}
                      className={`picture ${picture === p.key ? "on" : ""}`}
                      onClick={() =>
                        update((c) => {
                          for (const l of LANGS) c.text[l].illustrationKey = p.key;
                        })
                      }
                    >
                      <Illustration name={p.key} />
                      {p.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`picture ${!picture ? "on" : ""}`}
                    onClick={() =>
                      update((c) => {
                        for (const l of LANGS) delete c.text[l].illustrationKey;
                      })
                    }
                  >
                    <div style={{ height: 70, display: "grid", placeItems: "center" }} className="muted">
                      None
                    </div>
                    No picture
                  </button>
                </div>
              </div>
            </section>

            <div className="row" style={{ position: "sticky", top: 0, zIndex: 3, background: "var(--bg)", padding: "8px 0" }}>
              <span className="muted small">Writing in</span>
              <div className="segmented" role="group" aria-label="Language">
                {LANGS.map((l) => (
                  <button
                    type="button"
                    key={l}
                    className={lang === l ? "on" : ""}
                    onClick={() => setLang(l)}
                    title={languageComplete(tidy(content), l) ? "Complete" : "Something is missing"}
                  >
                    <span className={`dot ${languageComplete(tidy(content), l) ? "ok" : ""}`} />
                    {LANG_NAMES[l]}
                  </button>
                ))}
              </div>
            </div>

            <section className="card stack">
              <h2>Situation · {LANG_NAMES[lang]}</h2>
              <label className="field">
                Title
                <input
                  type="text"
                  lang={lang}
                  className={miss(scene.title)}
                  value={scene.title}
                  onChange={(e) => setScene(lang, "title", e.target.value)}
                />
              </label>
              <label className="field">
                What happens
                <span className="hint">Two or three short sentences, in everyday spoken words.</span>
                <textarea
                  lang={lang}
                  className={miss(scene.situation)}
                  value={scene.situation}
                  onChange={(e) => setScene(lang, "situation", e.target.value)}
                />
              </label>
              <AudioField
                folder={folder(lang)}
                name="situation"
                value={scene.situationAudio}
                disabled={readOnly}
                onChange={(p) => setScene(lang, "situationAudio", p)}
              />
            </section>

            {content.options.map((o, i) => {
              const t = o.text[lang];
              return (
                <section key={o.key} className={`card stack response ${o.quality}`}>
                  <div className="row" style={{ justifyContent: "space-between" }}>
                    <h2>
                      Response {o.key} · {LANG_NAMES[lang]}
                    </h2>
                    <div className="segmented" role="group" aria-label={`Response ${o.key} is`}>
                      {QUALITIES.map((q) => (
                        <button type="button" key={q} className={o.quality === q ? "on" : ""} onClick={() => setQuality(i, q)}>
                          {QUALITY_NAMES[q]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="field">
                    The choice
                    <input
                      type="text"
                      lang={lang}
                      className={miss(t.text)}
                      value={t.text}
                      onChange={(e) => setOption(i, lang, "text", e.target.value)}
                    />
                  </label>
                  <AudioField
                    folder={folder(lang)}
                    name={`option-${o.key}`}
                    value={t.audio}
                    disabled={readOnly}
                    onChange={(p) => setOption(i, lang, "audio", p)}
                  />
                  <label className="field">
                    What happens next
                    <textarea
                      lang={lang}
                      className={miss(t.outcome)}
                      value={t.outcome}
                      onChange={(e) => setOption(i, lang, "outcome", e.target.value)}
                    />
                  </label>
                  <AudioField
                    folder={folder(lang)}
                    name={`outcome-${o.key}`}
                    value={t.outcomeAudio}
                    disabled={readOnly}
                    onChange={(p) => setOption(i, lang, "outcomeAudio", p)}
                  />
                  <label className="field">
                    What went well
                    <span className="hint">Optional. One line each, shown after this choice.</span>
                    <textarea
                      lang={lang}
                      value={(t.feedbackWentWell ?? []).join("\n")}
                      onChange={(e) => setOption(i, lang, "feedbackWentWell", e.target.value.split("\n"))}
                    />
                  </label>
                  {o.quality === "best" && (
                    <>
                      <label className="field">
                        Rule to remember
                        <span className="hint">Shown after every choice, in the Remember card.</span>
                        <input
                          type="text"
                          lang={lang}
                          className={miss(t.keyRule)}
                          value={t.keyRule ?? ""}
                          onChange={(e) => setOption(i, lang, "keyRule", e.target.value)}
                        />
                      </label>
                      <label className="field">
                        You can say
                        <span className="hint">A sample line the person can use on site.</span>
                        <textarea
                          lang={lang}
                          className={miss(t.sampleLine)}
                          value={t.sampleLine ?? ""}
                          onChange={(e) => setOption(i, lang, "sampleLine", e.target.value)}
                        />
                      </label>
                      <AudioField
                        folder={folder(lang)}
                        name="sample-line"
                        value={t.sampleLineAudio}
                        disabled={readOnly}
                        onChange={(p) => setOption(i, lang, "sampleLineAudio", p)}
                      />
                    </>
                  )}
                </section>
              );
            })}

            {issues.length > 0 && !readOnly && (
              <div className="notice warn">
                <strong>Before it can go for review</strong>
                <ul>
                  {issues.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            )}

            {!readOnly && (
              <div className="sticky-actions">
                <button type="button" className="btn outline" disabled={busy || !dirty || !SLUG.test(content.id)} onClick={saveDraft}>
                  {row?.status === "approved" ? "Save changes" : "Save draft"}
                </button>
                {status === "draft" && (
                  <button type="button" className="btn" disabled={busy || issues.length > 0} onClick={sendForReview}>
                    Send for review
                  </button>
                )}
                {status === "in-review" && (
                  <>
                    {admin.can_approve && (
                      <button type="button" className="btn" disabled={busy || dirty || issues.length > 0} onClick={approve}>
                        Approve
                      </button>
                    )}
                    <button type="button" className="btn quiet" disabled={busy} onClick={backToDraft}>
                      Back to draft
                    </button>
                  </>
                )}
                {pending && (
                  <button type="button" className="btn quiet" disabled={busy} onClick={discard}>
                    Throw away changes
                  </button>
                )}
                {status === "in-review" && dirty && (
                  <span className="small muted">Saving an edit sends it back to draft.</span>
                )}
                {status === "in-review" && !admin.can_approve && (
                  <span className="small muted">Waiting for a training lead who can approve.</span>
                )}
              </div>
            )}
            {message && (
              <div className={`notice ${message.kind}`} role="status">
                {message.text}
                {message.list && (
                  <ul>
                    {message.list.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </fieldset>
        <div className="preview-col">
          <PhonePreview content={content} />
        </div>
      </div>
    </>
  );
}

function Skills({ tags, known, onChange }: { tags: string[]; known: string[]; onChange: (tags: string[]) => void }) {
  const [typed, setTyped] = useState("");
  const add = () => {
    const tag = skillTag(typed);
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setTyped("");
  };
  return (
    <div className="field">
      Skills it practises
      <span className="hint">These group scenarios in the "hardest" and "skills to strengthen" lists.</span>
      <div className="chips">
        {tags.map((t) => (
          <span key={t} className="chip">
            {skillName(t)}
            <button type="button" aria-label={`Remove ${skillName(t)}`} onClick={() => onChange(tags.filter((x) => x !== t))}>
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="row">
        <input
          type="text"
          list="known-skills"
          placeholder="For example: Visitor disputes"
          value={typed}
          className={tags.length ? "" : "missing"}
          style={{ flex: 1 }}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
        />
        <datalist id="known-skills">
          {known.filter((k) => !tags.includes(k)).map((k) => (
            <option key={k} value={skillName(k)} />
          ))}
        </datalist>
        <button type="button" className="btn quiet small" disabled={!typed.trim()} onClick={add}>
          Add skill
        </button>
      </div>
    </div>
  );
}
